import { useCreateAuditBatchForTaskIds } from 'react-scale/components/ExpertManagement/hooks/mutations/useCreateAuditBatchForTaskIds';
import { useFetchAttemptAudits } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchAttemptAudits';
import { useFetchAuditFromBatchId } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchAuditFromBatchId';
import {
  useAuditToTaskMap,
  AuditDto,
} from 'react-scale/components/ExpertManagement/stores/audit.store';

type BeginAuditParams = {
  customAuditUrl?: (auditBatchId: string) => string;
};

export const useBeginAudit = (
  { customAuditUrl }: BeginAuditParams = {
    customAuditUrl: undefined,
  },
): {
  beginAudit: ({
    batchId,
    projectId,
    taskIds,
    chatBulkAudit,
    auditGoal,
  }: {
    projectId: string;
    batchId?: string;
    taskIds?: string[];
    chatBulkAudit?: boolean;
    auditGoal?: string;
  }) => Promise<boolean | AuditDto | undefined>;
  beginAuditLoading: boolean;
} => {
  const auditToTaskMap = useAuditToTaskMap();
  const { isLoading: fetchBatchLoading, mutateAsync: fetchBatch } = useFetchAuditFromBatchId();
  const { isLoading: createBatchLoading, mutateAsync: createBatch } = useCreateAuditBatchForTaskIds(
    { customAuditUrl },
  );
  const { isLoading: fetchAttemptsLoading, mutateAsync: fetchAttemptAudits } =
    useFetchAttemptAudits();

  const beginAuditLoading = fetchBatchLoading || createBatchLoading || fetchAttemptsLoading;

  const beginAudit = async ({
    batchId,
    projectId,
    taskIds,
    chatBulkAudit,
    auditGoal,
  }: {
    projectId: string;
    batchId?: string;
    taskIds?: string[];
    chatBulkAudit?: boolean;
    auditGoal?: string;
  }): Promise<boolean | AuditDto | undefined> => {
    if ((!batchId && !taskIds) || (taskIds && taskIds.length === 0)) {
      return false;
    }

    let auditDto = undefined;

    if (chatBulkAudit && taskIds) {
      return await createBatch({
        projectId,
        taskIds,
        chatBulkAudit: chatBulkAudit ?? false,
        auditGoal,
      });
    } else if (batchId) {
      auditDto = await fetchBatch({ batchId, projectId });
    } else if (taskIds && taskIds.length === 1 && auditToTaskMap[taskIds[0]]) {
      auditDto = await fetchBatch({ batchId: auditToTaskMap[taskIds[0]], projectId });
    } else if (taskIds) {
      auditDto = await createBatch({ projectId, taskIds });
    }

    if (!auditDto) {
      return false;
    }

    await fetchAttemptAudits({ attemptIds: auditDto.attemptIds });
    return true;
  };

  return {
    beginAuditLoading,
    beginAudit,
  };
};
